import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tourScenes } from '../data/tourScenes';
import './Home.css';

const placements = ['left', 'right', 'left', 'right', 'left', 'right', 'left', 'right', 'center'];
const clamp = value => Math.max(0, Math.min(1, value));

function ScrollScene({ scene, index, onNext, last, onBack }) {
  const { t, i18n } = useTranslation();
  const section = useRef(null);
  const stage = useRef(null);
  const caption = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const copy = t(`tour.scenes.${scene.id}`, { returnObjects: true });
  const Heading = index === 0 ? 'h1' : 'h2';

  useEffect(() => {
    const element = section.current;
    const panel = caption.current;
    const viewport = stage.current;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const paint = () => {
      frame = 0;
      const height = window.innerHeight;
      const top = element.getBoundingClientRect().top;
      const progress = clamp(-top / (height * .52));
      const eased = progress * progress * (3 - 2 * progress);
      const alpha = clamp((progress - .12) / .45);
      viewport.style.setProperty('--caption-opacity', reduced.matches ? (progress > .75 ? 1 : 0) : alpha);
      viewport.style.setProperty('--caption-rise', `${reduced.matches ? 0 : (1 - eased) * (panel.getBoundingClientRect().height + 80)}px`);
      const nextVisible = progress > .8;
      panel.inert = !nextVisible;
      setRevealed(nextVisible);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(paint); };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    observer?.observe(panel);
    paint();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    reduced.addEventListener('change', schedule);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      reduced.removeEventListener('change', schedule);
    };
  }, []);

  const revealCaption = () => {
    const height = window.innerHeight;
    window.scrollTo({ top: window.scrollY + section.current.getBoundingClientRect().top + height * .54,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  return (
    <section ref={section} className={`sr-scroll-scene sr-scroll-scene--${scene.id}`} id={`scene-${scene.id}`} aria-label={copy.name}>
      <div ref={stage} className={`sr-scroll-stage sr-scroll-stage--${placements[index]}`}>
        <div className="sr-scroll-image">
          <picture>
          {scene.mobileSrc && <source media="(orientation: portrait)" srcSet={scene.mobileSrc} />}
          <img src={scene.src} alt={copy.alt} loading={index === 0 ? 'eager' : 'lazy'} fetchpriority={index === 0 ? 'high' : 'auto'} decoding="async" />
          </picture>
        </div>
        <button className="sr-scroll-cue" onClick={revealCaption} style={{ opacity: revealed ? 0 : 1, pointerEvents: revealed ? 'none' : 'auto' }} aria-hidden={revealed} tabIndex={revealed ? -1 : 0}>
          {t('tour.scrollExplore')} <span aria-hidden="true">↓</span>
        </button>
        <div ref={caption} className="sr-scroll-caption" aria-hidden={!revealed}>
          <p className="sr-scroll-eyebrow">{String(index + 1).padStart(2, '0')} / {copy.label}</p>
          <Heading>{copy.title}</Heading>
          <p className="sr-scroll-description">{copy.text}</p>
          <div className="sr-scroll-actions">
            {last ? <Link className="sr-scroll-primary" to="/contact">{t('tour.commission')} <span aria-hidden="true">↗</span></Link> :
              <button className="sr-scroll-primary" onClick={onNext}>{index === 0 ? t('tour.discover') : copy.next} <span aria-hidden="true">{i18n.dir() === 'rtl' ? '←' : '→'}</span></button>}
            {index > 0 && <button className="sr-scroll-back" onClick={onBack}>{t('tour.back')}</button>}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const [entered, setEntered] = useState(false);
  const pendingScene = useRef(null);
  const moveTo = id => {
    const element = document.getElementById(`scene-${id}`);
    if (!element) return;
    window.scrollTo({ top: window.scrollY + element.getBoundingClientRect().top, behavior: 'auto' });
    element.setAttribute('tabindex', '-1');
    element.focus({ preventScroll: true });
  };
  useLayoutEffect(() => {
    if (entered && pendingScene.current) {
      moveTo(pendingScene.current);
      pendingScene.current = null;
    }
  }, [entered]);
  const begin = () => {
    if (entered) moveTo('reception');
    else { pendingScene.current = 'reception'; setEntered(true); }
  };
  return (
    <main className="sr-scroll-tour" aria-label={t('tour.label')}>
      <ScrollScene scene={tourScenes[0]} index={0} onNext={begin} />
      {entered && tourScenes.slice(1).map((scene, offset) => {
        const index = offset + 1;
        return <ScrollScene key={scene.id} scene={scene} index={index} last={index === tourScenes.length - 1}
          onNext={() => moveTo(tourScenes[index + 1]?.id)} onBack={() => moveTo(tourScenes[index - 1].id)} />;
      })}
    </main>
  );
}
